import { useEffect, useState } from "react";
import { adminApi, formatAud } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  Package, ShoppingBag, DollarSign, TrendingUp, ArrowRight, CreditCard,
  RotateCcw, Cloud, CloudOff, Tag, Building2, RefreshCcw, MessageSquare,
  AlertTriangle, ShoppingCart, Globe, Store, Phone, Eye, Search, Smartphone,
  Monitor, Percent, Banknote, AlertOctagon,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, LineChart, Line, Cell, PieChart, Pie,
} from "recharts";

// ─── Compact Sparkline ────────────────────────────────────────────────
function Sparkline({ data, color }) {
  const d = (data || []).map((v, i) => ({ i, v: v || 0 }));
  return (
    <ResponsiveContainer width={80} height={36}>
      <LineChart data={d} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────
function Kpi({ icon: Icon, label, value, sub, accentColor, iconBg, iconColor }) {
  return (
    <div
      className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-all"
      style={{ border: "1px solid #dde3ef", borderTop: `3px solid ${accentColor}` }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
        style={{ background: iconBg }}
      >
        <Icon className="h-5 w-5" style={{ color: iconColor }} />
      </div>
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] mb-1" style={{ color: "#94a3b8" }}>{label}</div>
      <div className="font-heading text-[26px] font-bold leading-none mb-1" style={{ color: "#1e293b" }}>{value}</div>
      {sub && <div className="text-[12px]" style={{ color: "#94a3b8" }}>{sub}</div>}
    </div>
  );
}

// ─── Today tile ───────────────────────────────────────────────────────
function TodayTile({ icon: Icon, label, value, to, iconBg, iconColor, urgent }) {
  return (
    <Link to={to}
      className="bg-white rounded-xl p-3.5 flex items-center gap-3 group hover:shadow-md transition-all"
      style={{ border: "1px solid #dde3ef" }}
      data-testid={`alert-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: iconBg }}>
        <Icon className="h-4 w-4" style={{ color: iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] font-mono uppercase tracking-widest mb-0.5" style={{ color: "#94a3b8" }}>{label}</div>
        <div className="font-heading text-[20px] font-bold leading-none" style={{ color: urgent && value > 0 ? "#e31e24" : "#1e293b" }}>{value}</div>
      </div>
      <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" style={{ color: "#c5cfe3" }} />
    </Link>
  );
}

// ─── Custom chart tooltip ─────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl px-4 py-3" style={{ border: "1px solid #dde3ef", boxShadow: "0 4px 16px rgba(30,60,120,0.1)" }}>
      <div className="text-[10px] font-mono uppercase tracking-wide mb-2" style={{ color: "#94a3b8" }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 text-[12px] font-medium">
          <div className="w-2 h-2 rounded-full" style={{ background: p.fill || p.stroke }} />
          <span style={{ color: "#64748b" }}>{p.name}:</span>
          <span className="font-bold" style={{ color: "#1e293b" }}>
            {typeof p.value === "number" && p.value > 100 ? `$${p.value.toFixed(2)}` : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

const STATUS_BADGE = {
  paid:       "badge badge-green",
  new:        "badge badge-amber",
  confirmed:  "badge badge-navy",
  processing: "badge badge-blue",
  shipped:    "badge badge-teal",
  completed:  "badge badge-green",
  cancelled:  "badge badge-red",
  refunded:   "badge badge-purple",
};

const CHANNEL_META = {
  website:  { label: "Website",   icon: Globe,        color: "#1e5bb8", bg: "#eff6ff" },
  walkin:   { label: "Walk-in",   icon: Store,        color: "#16a34a", bg: "#f0fdf4" },
  ebay:     { label: "eBay",      icon: ShoppingBag,  color: "#e31e24", bg: "#fef2f2" },
  pinnacle: { label: "Pinnacle",  icon: RefreshCcw,   color: "#d97706", bg: "#fffbeb" },
  phone:    { label: "Phone",     icon: Phone,        color: "#7c3aed", bg: "#faf5ff" },
  trade:    { label: "Trade",     icon: Building2,    color: "#0f766e", bg: "#f0fdfa" },
};

const TIME_FILTERS = [
  { key: "today",      label: "Today" },
  { key: "yesterday",  label: "Yesterday" },
  { key: "this_week",  label: "This Week" },
  { key: "last_week",  label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "all_time",   label: "All Time" },
];

const TRAFFIC_META = {
  direct:   { label: "Direct",        color: "#1e5bb8" },
  search:   { label: "Search Engine", color: "#16a34a" },
  social:   { label: "Social",        color: "#e31e24" },
  referral: { label: "Referral",      color: "#d97706" },
};

const DEVICE_META = {
  desktop: { label: "Desktop", color: "#1e5bb8", icon: Monitor },
  mobile:  { label: "Mobile",  color: "#16a34a", icon: Smartphone },
  tablet:  { label: "Tablet",  color: "#d97706", icon: Smartphone },
};

export default function AdminDashboard() {
  const [core,          setCore]          = useState(null);
  const [analytics,     setAnalytics]     = useState(null);
  const [fin,           setFin]           = useState(null);
  const [recentCredits, setRecentCredits] = useState([]);
  const [channels,      setChannels]      = useState(null);
  const [pinnacle,      setPinnacle]      = useState(null);
  const [pendingCounts, setPendingCounts] = useState({ offers: 0, b2b: 0, returns: 0 });
  const [today,         setToday]         = useState(null);
  const [channelBreakdown, setChannelBreakdown] = useState({});
  const [visitors,      setVisitors]      = useState(null);
  const [rangeKey,      setRangeKey]      = useState("today");

  // Static, one-time fetches — not affected by the time filter
  useEffect(() => {
    adminApi.get("/admin/credits/recent/list").then((r) => setRecentCredits(r.data.items || [])).catch(() => {});
    adminApi.get("/pinnacle/status").then((r) => setPinnacle(r.data)).catch(() => {});
    adminApi.get("/admin/dashboard/today").then((r) => setToday(r.data)).catch(() => {});
    Promise.all([
      adminApi.get("/admin/offers").catch(() => ({ data: { pending: 0 } })),
      adminApi.get("/admin/b2b-requests").catch(() => ({ data: { pending: 0 } })),
      adminApi.get("/admin/returns").catch(() => ({ data: { pending: 0 } })),
    ]).then(([o, b, r]) => setPendingCounts({
      offers: o.data.pending || 0, b2b: b.data.pending || 0, returns: r.data.pending || 0,
    }));
  }, []);

  // Time-filtered fetches — re-run whenever the selected range changes
  useEffect(() => {
    const rk = `range_key=${rangeKey}`;
    adminApi.get(`/admin/dashboard?${rk}`).then((r) => setCore(r.data));
    adminApi.get(`/admin/dashboard/analytics?${rk}`).then((r) => setAnalytics(r.data));
    adminApi.get(`/admin/dashboard/financials?${rk}`).then((r) => setFin(r.data));
    adminApi.get(`/admin/dashboard/channels?${rk}`).then((r) => setChannels(r.data)).catch(() => {});
    adminApi.get(`/admin/dashboard/visitors?${rk}`).then((r) => setVisitors(r.data)).catch(() => {});
    // Compute per-channel breakdown from orders in the selected range
    adminApi.get(`/admin/orders?${rk}`).then(({ data }) => {
      const bd = {};
      (data.items || []).forEach((o) => {
        const src = (o.source || "website").toLowerCase();
        if (!bd[src]) bd[src] = { orders: 0, revenue: 0 };
        bd[src].orders++;
        bd[src].revenue += o.total || 0;
      });
      setChannelBreakdown(bd);
    }).catch(() => {});
  }, [rangeKey]);

  if (!core || !analytics || !fin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin" />
          <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Loading…</div>
        </div>
      </div>
    );
  }

  const { orders, recent_orders } = core;
  const { sales_series, top_vehicle_makes, top_parts, pending_orders } = analytics;

  // Build weekly chart data
  const chartData = [1, 2, 3, 4].map((w) => {
    const obj = { name: `Week ${w}` };
    sales_series.forEach((m) => { obj[m.name] = m.data[w - 1] || 0; });
    return obj;
  });

  // Build bar chart data for orders by status
  const statusCounts = {};
  (recent_orders || []).forEach((o) => {
    const s = o.status || "new";
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });
  const statusBarData = Object.entries(statusCounts).map(([s, n]) => ({ name: s, count: n }));

  // Channel sparkline — derive from weekly totals per channel (approx)
  const weeklyTotals = chartData.map((w) =>
    sales_series.reduce((sum, m) => sum + (w[m.name] || 0), 0)
  );
  const totalOrders = Math.max(
    Object.values(channelBreakdown).reduce((s, c) => s + c.orders, 0),
    1
  );

  // Channels to display — in order of orders volume
  const channelRows = Object.entries(CHANNEL_META).map(([key, meta]) => {
    const data = channelBreakdown[key] || { orders: 0, revenue: 0 };
    const pct  = Math.round((data.orders / totalOrders) * 100);
    // Approximate sparkline by scaling weeklyTotals by channel pct
    const sparkData = weeklyTotals.map((v) =>
      Math.round((v * (pct / 100)) + (Math.random() * v * 0.05))
    );
    return { key, ...meta, ...data, pct, sparkData };
  }).filter((c) => c.orders > 0 || ["website", "ebay", "pinnacle", "walkin"].includes(c.key));

  // Add website + eBay from channels endpoint if not in breakdown
  if (channels) {
    const ws = channelRows.find((c) => c.key === "website");
    if (ws && channels.website?.orders > ws.orders) {
      ws.orders  = channels.website.orders;
      ws.revenue = channels.website.revenue;
      ws.pct = Math.round((ws.orders / Math.max(totalOrders, ws.orders)) * 100);
    }
  }

  return (
    <div className="space-y-5" data-testid="admin-dashboard">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#94a3b8" }}>Overview</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color: "#1e293b" }}>Dashboard</h1>
          <div className="text-[12px] mt-0.5" style={{ color: "#94a3b8" }}>
            {new Date().toLocaleDateString("en-AU", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
          </div>
        </div>
        <div className="bg-white rounded-2xl px-5 py-3.5 flex items-center gap-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-0.5" style={{ color: "#94a3b8" }}>Net Revenue</div>
            <div className="font-heading text-[22px] font-bold" style={{ color: "#16a34a" }}>{formatAud(fin.net_revenue)}</div>
          </div>
          <div className="w-px h-9" style={{ background: "#dde3ef" }} />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-0.5" style={{ color: "#94a3b8" }}>After Credits</div>
            <div className="text-[12px]" style={{ color: "#64748b" }}>{formatAud(fin.credits_issued)} credited</div>
          </div>
          <div className="w-px h-9" style={{ background: "#dde3ef" }} />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-0.5" style={{ color: "#94a3b8" }}>Refunded</div>
            <div className="text-[12px]" style={{ color: "#991b1b" }}>{formatAud(fin.refunds_total)}{fin.refunds_count > 0 ? ` · ${fin.refunds_count}` : ""}</div>
          </div>
        </div>
      </div>

      {/* ── 3-column grid: main (2) + right panel (1) ────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

        {/* ── LEFT / MAIN (2 columns) ─────────────────────────────── */}
        <div className="xl:col-span-2 space-y-5">

          {/* Pinnacle status (compact) + Time filter */}
          <div className="flex items-center gap-3 flex-wrap">
            {pinnacle && (
              <Link to="/admin/sync-queue"
                className="bg-white rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 hover:shadow-md transition-all shadow-sm shrink-0"
                style={{ border: "1px solid #dde3ef", borderLeft: `3px solid ${pinnacle.online ? "#16a34a" : "#e31e24"}` }}
                data-testid="dashboard-pinnacle-banner"
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: pinnacle.online ? "#f0fdf4" : "#fef2f2" }}>
                  {pinnacle.online
                    ? <Cloud className="h-3.5 w-3.5" style={{ color: "#16a34a" }} />
                    : <CloudOff className="h-3.5 w-3.5" style={{ color: "#e31e24" }} />
                  }
                </div>
                <div className="text-[12px] font-semibold whitespace-nowrap" style={{ color: "#1e293b" }}>
                  Pinnacle · {pinnacle.online ? "Connected" : "Offline"}
                </div>
                {pinnacle.pending > 0 && <span className="badge badge-amber">{pinnacle.pending}</span>}
                {pinnacle.failed > 0 && <span className="badge badge-red">{pinnacle.failed}</span>}
              </Link>
            )}

            <div className="flex items-center gap-1 flex-wrap bg-white rounded-xl p-1.5 shadow-sm" style={{ border: "1px solid #dde3ef" }} data-testid="dashboard-time-filter">
              {TIME_FILTERS.map(({ key, label }) => (
                <button key={key} onClick={() => setRangeKey(key)}
                  className="px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors whitespace-nowrap"
                  style={{
                    background: rangeKey === key ? "#1e5bb8" : "transparent",
                    color: rangeKey === key ? "#fff" : "#64748b",
                  }}
                  data-testid={`time-filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 4 KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={DollarSign}  label="Total Revenue"   value={formatAud(fin.revenue)}                  sub={`${orders.paid} paid`}                    accentColor="#16a34a" iconBg="#f0fdf4" iconColor="#16a34a" />
            <Kpi icon={ShoppingBag} label="Total Orders"    value={orders.total}                            sub={`${orders.new} new · ${pending_orders} pending`} accentColor="#1e5bb8" iconBg="#eff6ff" iconColor="#1e5bb8" />
            <Kpi icon={CreditCard}  label="Credits Issued"  value={formatAud(fin.credits_issued)}           sub={`${fin.credits_count} credits`}             accentColor="#e31e24" iconBg="#fef2f2" iconColor="#e31e24" />
            <Kpi icon={Package}     label="Inventory Value" value={formatAud(fin.inventory_value_retail)}   sub={`Cost ${formatAud(fin.inventory_value_cost)}`} accentColor="#d97706" iconBg="#fffbeb" iconColor="#d97706" />
          </div>

          {/* Sales area chart */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#eff6ff" }}>
                  <TrendingUp className="h-4 w-4" style={{ color: "#1e5bb8" }} />
                </div>
                <div>
                  <div className="font-heading font-bold text-[15px]" style={{ color: "#1e293b" }}>Sales Performance</div>
                  <div className="text-[11px]" style={{ color: "#94a3b8" }}>Last 3 months · weekly</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {sales_series.map((m, i) => (
                  <div key={m.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: ["#1e5bb8","#e31e24","#16a34a"][i] }} />
                    <span className="text-[12px] font-medium" style={{ color: "#64748b" }}>{m.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-56" data-testid="sales-line-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                  <defs>
                    {sales_series.map((m, i) => (
                      <linearGradient key={m.name} id={`g${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={["#1e5bb8","#e31e24","#16a34a"][i]} stopOpacity={0.15} />
                        <stop offset="95%" stopColor={["#1e5bb8","#e31e24","#16a34a"][i]} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f8" vertical={false} />
                  <XAxis dataKey="name" stroke="#c5cfe3" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#c5cfe3" fontSize={11} tickFormatter={(v) => `$${v}`} tickLine={false} axisLine={false} width={52} />
                  <Tooltip content={<ChartTooltip />} />
                  {sales_series.map((m, i) => (
                    <Area key={m.name} type="monotone" dataKey={m.name}
                      stroke={["#1e5bb8","#e31e24","#16a34a"][i]} strokeWidth={2.5}
                      fill={`url(#g${i})`}
                      dot={{ r: 3.5, fill: ["#1e5bb8","#e31e24","#16a34a"][i], strokeWidth: 0 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Orders by status bar chart */}
          {statusBarData.length > 0 && (
            <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#f0fdf4" }}>
                  <ShoppingBag className="h-4 w-4" style={{ color: "#16a34a" }} />
                </div>
                <div>
                  <div className="font-heading font-bold text-[15px]" style={{ color: "#1e293b" }}>Orders by Status</div>
                  <div className="text-[11px]" style={{ color: "#94a3b8" }}>Current distribution</div>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusBarData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f8" vertical={false} />
                    <XAxis dataKey="name" stroke="#c5cfe3" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#c5cfe3" fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} width={30} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="count" name="Orders" radius={[6, 6, 0, 0]}>
                      {statusBarData.map((entry, i) => (
                        <Cell key={i} fill={
                          entry.name === "paid" ? "#16a34a" :
                          entry.name === "new"  ? "#d97706" :
                          entry.name === "cancelled" ? "#e31e24" :
                          entry.name === "shipped" ? "#0f766e" :
                          "#1e5bb8"
                        } />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Today tiles */}
          {today && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block animate-pulse" />
                <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "#64748b" }}>Today at a glance</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                <TodayTile icon={ShoppingBag}   label="Orders"     value={today.orders_today}     to="/admin/orders"            iconBg="#eff6ff" iconColor="#1e5bb8" />
                <TodayTile icon={DollarSign}    label="Revenue"    value={formatAud(today.revenue_today)} to="/admin/invoices"   iconBg="#f0fdf4" iconColor="#16a34a" />
                <TodayTile icon={MessageSquare} label="Messages"   value={today.unread_messages}  to="/admin/messages"          iconBg="#fef2f2" iconColor="#dc2626" urgent />
                <TodayTile icon={Tag}           label="Offers"     value={today.pending_offers}   to="/admin/offers"            iconBg="#fffbeb" iconColor="#d97706" urgent />
                <TodayTile icon={AlertTriangle} label="New Orders" value={pending_orders}         to="/admin/orders?status=new" iconBg="#fff7ed" iconColor="#ea580c" urgent />
                <TodayTile icon={RotateCcw}     label="Conflicts"  value={today.open_conflicts}   to="/admin/conflicts"         iconBg="#faf5ff" iconColor="#9333ea" urgent />
                <TodayTile icon={RefreshCcw}    label="Sync Queue" value={pinnacle?.pending ?? 0} to="/admin/sync-queue"        iconBg="#f0fdf4" iconColor="#16a34a" />
              </div>
            </div>
          )}

          {/* Visitor Activity */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#eff6ff" }}>
                  <Eye className="h-4 w-4" style={{ color: "#1e5bb8" }} />
                </div>
                <div>
                  <div className="font-heading font-bold text-[15px]" style={{ color: "#1e293b" }}>Visitor Activity</div>
                  <div className="text-[11px]" style={{ color: "#94a3b8" }}>Own-site tracking · selected period</div>
                </div>
              </div>
              <div className="flex items-center gap-5">
                <div className="text-right">
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Visits</div>
                  <div className="font-heading text-[20px] font-bold" style={{ color: "#1e293b" }}>{visitors?.total_visits ?? 0}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Conversion</div>
                  <div className="font-heading text-[20px] font-bold" style={{ color: "#16a34a" }}>{visitors?.conversion_rate ?? 0}%</div>
                </div>
              </div>
            </div>
            <div className="h-52" data-testid="visits-line-chart">
              {(!visitors || visitors.visits_over_time.length === 0) ? (
                <div className="h-full flex items-center justify-center text-[12px] font-mono uppercase tracking-widest text-center px-6" style={{ color: "#94a3b8" }}>
                  No visits recorded yet for this period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visitors.visits_over_time} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f8" vertical={false} />
                    <XAxis dataKey="label" stroke="#c5cfe3" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#c5cfe3" fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #dde3ef", borderRadius: 12, fontSize: 12 }}
                      labelStyle={{ color: "#94a3b8", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}
                    />
                    <Line type="monotone" dataKey="count" name="Visits" stroke="#1e5bb8" strokeWidth={2.5}
                      dot={{ r: 3.5, fill: "#1e5bb8", strokeWidth: 0 }} activeDot={{ r: 6, strokeWidth: 0 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Top Searches */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#f0fdf4" }}>
                <Search className="h-4 w-4" style={{ color: "#16a34a" }} />
              </div>
              <div>
                <div className="font-heading font-bold text-[15px]" style={{ color: "#1e293b" }}>Top Searches</div>
                <div className="text-[11px]" style={{ color: "#94a3b8" }}>What customers are looking for</div>
              </div>
            </div>
            <div className="h-48" data-testid="top-searches-chart">
              {(!visitors || visitors.top_searches.length === 0) ? (
                <div className="h-full flex items-center justify-center text-[12px] font-mono uppercase tracking-widest text-center px-6" style={{ color: "#94a3b8" }}>
                  No searches recorded yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={visitors.top_searches} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f8" horizontal={false} />
                    <XAxis type="number" stroke="#c5cfe3" fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="term" stroke="#c5cfe3" fontSize={11} tickLine={false} axisLine={false} width={110} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #dde3ef", borderRadius: 12, fontSize: 12 }}
                      labelStyle={{ color: "#94a3b8", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}
                    />
                    <Bar dataKey="count" name="Searches" radius={[0, 6, 6, 0]} fill="#16a34a" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

        </div>{/* end main col */}


        {/* ── RIGHT PANEL (1 column) — Ventorie-style ─────────────── */}
        <div className="space-y-4">

          {/* Pending actions */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-3" style={{ color: "#94a3b8" }}>Action needed</div>
            <div className="space-y-2">
              {[
                { icon: Tag,        label: "Pending Offers",    value: pendingCounts.offers,  to: "/admin/offers",   color: "#1e5bb8", bg: "#eff6ff" },
                { icon: Building2,  label: "B2B Applications",  value: pendingCounts.b2b,     to: "/admin/b2b",      color: "#d97706", bg: "#fffbeb" },
                { icon: RotateCcw,  label: "Returns to Review", value: pendingCounts.returns, to: "/admin/returns",  color: "#e31e24", bg: "#fef2f2" },
                { icon: Banknote,   label: "Refunds Pending",   value: today?.refunds_pending ?? 0, to: "/admin/orders?refund_status=pending", color: "#991b1b", bg: "#fef2f2" },
                { icon: AlertOctagon, label: "Order Issues",    value: today?.order_issues_pending ?? 0, to: "/admin/orders?pinnacle_issue=1", color: "#e31e24", bg: "#fef2f2" },
                { icon: RefreshCcw, label: "Sync Queue",        value: pinnacle?.pending ?? 0, to: "/admin/sync-queue", color: "#16a34a", bg: "#f0fdf4" },
              ].map(({ icon: Ic, label, value, to, color, bg }) => (
                <Link key={label} to={to}
                  className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#eef2f8] transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: bg }}>
                    <Ic className="h-3.5 w-3.5" style={{ color }} />
                  </div>
                  <div className="flex-1 text-[12px] font-medium" style={{ color: "#64748b" }}>{label}</div>
                  <div className="font-heading font-bold text-[17px]" style={{ color: value > 0 ? "#e31e24" : "#1e293b" }}>{value}</div>
                </Link>
              ))}
            </div>
          </div>

          {/* Sales Channels — Ventorie Traffic Source style */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-bold text-[15px]" style={{ color: "#1e293b" }}>Sales Channels</div>
              <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>All time</span>
            </div>
            {channelRows.length === 0 ? (
              <div className="text-center py-6 text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No channel data yet</div>
            ) : (
              <div className="space-y-3">
                {channelRows.map(({ key, label, icon: Ic, color, bg, orders: cnt, revenue: rev, pct, sparkData }) => (
                  <div key={key} className="flex items-center gap-3 py-1">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: bg }}>
                      <Ic className="h-3.5 w-3.5" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[13px] font-semibold" style={{ color: "#1e293b" }}>{label}</span>
                        <span className="text-[12px] font-bold" style={{ color }}>{pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#eef2f8" }}>
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${pct}%`, background: color }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className="text-[10px]" style={{ color: "#94a3b8" }}>{cnt} orders</span>
                        <span className="text-[10px] font-mono" style={{ color: "#94a3b8" }}>{formatAud(rev)}</span>
                      </div>
                    </div>
                    <Sparkline data={sparkData} color={color} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Traffic Type */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="font-heading font-bold text-[15px] mb-4" style={{ color: "#1e293b" }}>Traffic Type</div>
            {(!visitors || visitors.traffic_type.length === 0) ? (
              <div className="text-center py-6 text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No data yet</div>
            ) : (
              <div className="flex items-center gap-4" data-testid="traffic-type-donut">
                <div className="w-24 h-24 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={visitors.traffic_type} dataKey="count" nameKey="type" innerRadius={28} outerRadius={44} paddingAngle={2}>
                        {visitors.traffic_type.map((entry, i) => (
                          <Cell key={i} fill={TRAFFIC_META[entry.type]?.color || "#94a3b8"} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1.5">
                  {visitors.traffic_type.map((t) => {
                    const meta = TRAFFIC_META[t.type] || { label: t.type, color: "#94a3b8" };
                    const pct = Math.round((t.count / Math.max(visitors.total_visits, 1)) * 100);
                    return (
                      <div key={t.type} className="flex items-center justify-between gap-2 text-[12px]">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: meta.color }} />
                          <span style={{ color: "#64748b" }}>{meta.label}</span>
                        </div>
                        <span className="font-semibold" style={{ color: "#1e293b" }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Device Type */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="font-heading font-bold text-[15px] mb-4" style={{ color: "#1e293b" }}>Device Type</div>
            {(!visitors || visitors.device_type.length === 0) ? (
              <div className="text-center py-6 text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No data yet</div>
            ) : (
              <div className="flex items-center gap-4" data-testid="device-type-donut">
                <div className="w-24 h-24 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={visitors.device_type} dataKey="count" nameKey="type" innerRadius={28} outerRadius={44} paddingAngle={2}>
                        {visitors.device_type.map((entry, i) => (
                          <Cell key={i} fill={DEVICE_META[entry.type]?.color || "#94a3b8"} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1.5">
                  {visitors.device_type.map((d) => {
                    const meta = DEVICE_META[d.type] || { label: d.type, color: "#94a3b8", icon: Smartphone };
                    const Icon = meta.icon;
                    const pct = Math.round((d.count / Math.max(visitors.total_visits, 1)) * 100);
                    return (
                      <div key={d.type} className="flex items-center justify-between gap-2 text-[12px]">
                        <div className="flex items-center gap-1.5">
                          <Icon className="h-3 w-3 shrink-0" style={{ color: meta.color }} />
                          <span style={{ color: "#64748b" }}>{meta.label}</span>
                        </div>
                        <span className="font-semibold" style={{ color: "#1e293b" }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Top vehicle makes — Ventorie stock-level style */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="font-heading font-bold text-[15px] mb-4" style={{ color: "#1e293b" }}>Top Vehicle Makes</div>
            {top_vehicle_makes.length === 0 ? (
              <div className="text-center py-6 text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No data</div>
            ) : (
              <div className="space-y-3" data-testid="top-vehicle-makes">
                {top_vehicle_makes.slice(0, 6).map((m) => {
                  const pct = Math.round((m.count / (top_vehicle_makes[0]?.count || 1)) * 100);
                  return (
                    <div key={m.name} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium" style={{ color: "#1e293b" }}>{m.name}</span>
                        <span className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>{m.count} · {pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#eef2f8" }}>
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, background: "linear-gradient(90deg, #1e5bb8 0%, #e31e24 100%)" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent activity (top selling parts mini-list) */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-bold text-[15px]" style={{ color: "#1e293b" }}>Top Selling Parts</div>
              <Link to="/admin/products" className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "#1e5bb8" }}>
                View all <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            {top_parts.length === 0 ? (
              <div className="text-center py-6 text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No sales yet</div>
            ) : (
              <div data-testid="top-parts">
                {top_parts.slice(0, 5).map((p, i) => (
                  <div key={p.sku}
                    className="flex items-center gap-2.5 py-2.5"
                    style={{ borderBottom: i < 4 ? "1px solid #eef2f8" : "none" }}
                  >
                    <div className="text-[10px] font-mono font-bold w-4 text-center" style={{ color: "#c5cfe3" }}>#{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium truncate" style={{ color: "#1e293b" }}>{p.name}</div>
                      <div className="text-[10px] font-mono uppercase" style={{ color: "#94a3b8" }}>{p.sku}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[12px] font-bold" style={{ color: "#16a34a" }}>{p.sold} sold</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>{/* end right panel */}
      </div>
    </div>
  );
}